import React, { useState, useEffect } from 'react';

import { CAMERA_ROTATION_OPTIONS } from '@vlabs/api-bindings/src/constants';
import { RegionsEditModal } from '@vlabs/monokit/pages/regions-edit-modal';
import { useHlsWithRetry } from '@vlabs/monokit/shared/lib/video/useHlsWithRetry';
import { CropIcon } from '@vlabs/icons';
import { Control, useWindowSize } from '@vlabs/uikit';
import cn from 'classnames';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AnalyticsDropdown } from '@vlabs/pages/video-analytics/streams/components/AnalyticsDropdown';

import st from './RegionsEdit.module.sass';
import { defaultValues } from '../../defaultFormValues';

const EMPTY_DROI = { mode: 'abs', form: 'wkt', area: '' };

export function RegionsEdit() {
  const { t } = useTranslation();
  const windowSize = useWindowSize();
  const { setValue } = useFormContext();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentAnalyticIndex, setCurrentAnalyticIndex] = useState(null);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [error, setError] = useState(false);

  const {
    analytics,
    initialRotationDeg,
    availableAnalyticsList,
    isVideoStream,
  } = useWatch({
    name: ['analytics', 'data'],
    compute: ([streamAnalytics, streamData]) => {
      return {
        analytics: streamAnalytics,
        initialRotationDeg: streamData?.rotation?.value,
        availableAnalyticsList: streamAnalytics?.map(({ analytic_name }) => analytic_name),
        isVideoStream: streamData?.type?.value === 'stream',
      };
    },
  });

  const { streamId } = useParams();
  const { status, retransmission } = useHlsWithRetry(shouldOpenModal && streamId);

  const resetModalState = () => {
    setIsModalVisible(false);
    setShouldOpenModal(false);
    setCurrentAnalyticIndex(null);
  };

  useEffect(() => {
    if (status === 'loaded') setIsModalVisible(true);
    if (status === 'failed') {
      resetModalState();
      setError(true);
    }
  }, [status]);

  const onSelectAnalytic = (type, index) => {
    setError(false);
    setShouldOpenModal(true);
    setCurrentAnalyticIndex(index);
  };

  const handleEditData = ({ roi, droi, rotationDeg }) => {
    if (rotationDeg !== initialRotationDeg) {
      analytics.forEach((analytic, index) => {
        setValue(`analytics.${index}.parameters.parameters.roi`, defaultValues.roi);
        setValue(`analytics.${index}.parameters.parameters.droi`, EMPTY_DROI);
      });
    }

    setValue(`analytics.${currentAnalyticIndex}.parameters.parameters.roi`, roi);
    setValue(`analytics.${currentAnalyticIndex}.parameters.parameters.droi`, droi || EMPTY_DROI);
    setValue('data.rotation', CAMERA_ROTATION_OPTIONS[rotationDeg]);

    resetModalState();
  };

  return (
    <div className={st.Root}>
      <h6>{t('videoAnalytics:editAndCreateStreamsPageTitles.frame_and_zones_settings')}</h6>

      <AnalyticsDropdown
        availableAnalyticsList={availableAnalyticsList}
        buttonSize="small"
        onAnalyticClick={onSelectAnalytic}
        renderTrigger={(props) => (
          <Control.Button
            className={cn(st.SetRegionsButton, {
              [st.SetRegionsButton_large]: windowSize.width >= 1920,
            })}
            disabled={!isVideoStream}
            fullWidth
            icon={<CropIcon />}
            variant="dimmed"
            {...props}
          >
            {t('btn.set_up')}
          </Control.Button>
        )}
        withOrdinals
      />

      {!!error && (
        <div className={st.Error}>
          <span className={st.Error__Title}>{t('videoAnalytics:error.failed_load_retransmission.title')}</span>
          <span>{t('videoAnalytics:error.failed_load_retransmission.message')}</span>
        </div>
      )}

      <RegionsEditModal
        droi={analytics?.[currentAnalyticIndex]?.parameters?.parameters?.droi}
        handleEditData={handleEditData}
        initialRotationDeg={initialRotationDeg}
        isOpen={isModalVisible}
        key={isModalVisible}
        onClose={resetModalState}
        retransmission={retransmission}
        roi={analytics?.[currentAnalyticIndex]?.parameters?.parameters?.roi}
      />
    </div>
  );
}
